import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Text, ButtonCard, Spacing, MessageBox } from '@reservamos/elements';
import { Trans, useTranslation } from 'react-i18next';
import { useGrowthBook } from '@growthbook/growthbook-react';
import paypal from 'images/payment/paypal.svg';
import coppelPay from 'images/payment/coppelpay.png';
import oxxo from 'images/payment/oxxo.svg';
import tarjetaNoAmex from 'images/payment/tarjeta-2.svg';
import tarjeta from 'images/payment/tarjeta.svg';
import tarjetaNoMC from 'images/payment/tarjeta-visa-amex.svg';
import tarjetaVisa from 'images/payment/visa-2.svg';
import tarjetaMc from 'images/payment/mastercard.svg';
import tarjetaPt from 'images/payment/tarjeta-pt.svg';
import pse from 'images/payment/pse.png';
import kueskipay from 'images/payment/kueskipay.svg';
import aplazo from 'images/payment/aplazo-blue.svg';
import mercadopago from 'images/payment/mercadopago.svg';
import isotypeSeven from 'images/payment/seven.png';
import isotypeAurrera from 'images/payment/isotype-aurrera.png';
import isotypeCirclek from 'images/payment/isotype-circlek.png';
import isotypeSoriana from 'images/payment/isotype-soriana.png';
import isotypeWallmart from 'images/payment/isotype-wallmart.png';
import pixLogo from 'images/payment/pix.png';
import efectyLogo from 'images/payment/efecty.png';
import nequiLogo from 'images/payment/nequi.svg';
import viajamas from 'images/brands-unique/etn/viajamas.png';
import applePayLogo from 'images/payment/apple_pay.svg';
import googlePayLogo from 'images/payment/google-pay.svg';
import moveObjToFirst from 'utils/sortObjectArray';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import useGrowthBookFeaturesValue from 'components/GrowthBookProvider/useGrowthBookFeaturesValues';
import { checkPaypalEligibility } from '@/actions/paypal';
import { isPayPalV6Enabled } from 'utils/paypalV6Config';
import { isApplePayAvailable } from 'utils/applePay';
import BadgeNew from './BadgeNew';
import './PaymentSelector.scss';
import yape from '../../images/payment/yuno/yape.png';

// Constants moved outside component to prevent recreation on each render
// These are the logos for different store payment options shown in the paycash method
const PAY_CASH_IMAGES = [
  isotypeAurrera,
  isotypeSeven,
  isotypeCirclek,
  isotypeSoriana,
  isotypeWallmart,
];

// Mapping from Yuno API payment categories to our internal payment types
// This mapping ensures consistent payment type usage across the application
const CATEGORY_TYPE_MAP = {
  ticket: 'store', // Physical store payments like OXXO
  payment_link: 'transfer', // Bank transfers
  buy_now_pay_later: 'bnpl', // Buy now, pay later options
};

// Payment types that can come with engine 'stripe' (card + wallets).
// The single `use_stripe` A/B flag filters all of them by type.
const STRIPE_CAPABLE_TYPES = ['credit_card', 'google_pay', 'apple_pay'];

/**
 * PaymentSelector component allows users to select a payment method.
 *
 * This component handles multiple payment sources:
 * 1. Legacy availablePayments (string array)
 * 2. New paymentMethods structure (objects with type/provider)
 * 3. Yuno payment gateway methods
 *
 * It normalizes these different structures into a unified format
 * and renders them as selectable payment options.
 *
 * @param {Object} props - Component props.
 * @param {string[]} props.availablePayments - List of available payment methods (legacy format).
 * @param {Object[]} props.paymentMethods - List of payment method objects (new format).
 * @param {string} props.selectedOption - Currently selected payment method (legacy format).
 * @param {Function} props.onPaymentTypeChange - Callback function when payment type changes.
 * @returns {React.Component} A component that renders the payment options.
 */
const PaymentSelector = ({
  availablePayments,
  paymentMethods = [],
  selectedOption,
  onPaymentTypeChange,
}) => {
  // Get white-label configuration and feature flags
  const { supportAmexCard, defaultPaymentOption, brand, stripe } = useWhitelabelEnvs();
  const features = useWhitelabelFeatures();

  // Redux hooks
  const dispatch = useDispatch();

  // Get selected payment method from Redux store
  const selectedPaymentMethod = useSelector((state) => state.purchase.get('selectedPaymentMethod'));

  // Get Yuno payment gateway configuration from Redux store
  const yuno = useSelector((state) => state.purchase.get('yuno'));

  // Get PayPal eligibility status from Redux store
  const paypalEligibility = useSelector((state) => state.purchase.get('paypalEligibility'));
  const isPaypalEligibilityLoading = paypalEligibility?.get('isLoading') ?? false;

  // Internationalization hook
  const { t } = useTranslation('payment');

  // Feature flag for showing Yape logo
  const { showYapeLogo } = useGrowthBookFeaturesValue('show_yape_logo');

  // A/B test flag: when enabled, the Stripe card engine is shown instead of the
  // tenant's default card engine (only if Stripe is actually available)
  const { useStripe } = useGrowthBookFeaturesValue('use_stripe');

  // GrowthBook instance to know if the experiment value is already resolved.
  // "Settled" means ready, or GrowthBook disabled for the tenant (never ready).
  const growthBook = useGrowthBook();
  const growthBookSettled = growthBook?.ready || !growthBook;

  // Track if initial payment selection has been done
  const hasInitializedRef = useRef(false);

  /**
   * Determines the appropriate card logo based on available card brands and Amex support.
   * Different logos are shown depending on which card types are supported.
   *
   * @returns {string} The path to the card logo image.
   */
  const getCardLogo = useCallback(() => {
    const hasCybersourceBrands = features.CYBERSOURCE_AVAILABLE_CARD_BRANDS?.length > 0;
    const supportsMastercard = features.CYBERSOURCE_AVAILABLE_CARD_BRANDS?.includes('mastercard');
    const supportsVisa = features.CYBERSOURCE_AVAILABLE_CARD_BRANDS?.includes('visa');
    const supportsAmex = features.CYBERSOURCE_AVAILABLE_CARD_BRANDS?.includes('amex');

    if (hasCybersourceBrands) {
      if (supportsMastercard && supportsVisa && !supportsAmex) {
        return tarjetaNoAmex; // Show logo with Visa and Mastercard
      }

      if (!supportsMastercard && supportsVisa && supportsAmex) {
        return tarjetaNoMC; // Show logo with Visa and Amex
      }

      if (!supportsMastercard && supportsVisa && !supportsAmex) {
        return tarjetaVisa; // Show logo with Visa only
      }

      if (supportsMastercard && !supportsVisa && !supportsAmex) {
        return tarjetaMc; // Show logo with Mastercard only
      }

      return tarjeta; // Show logo with Amex, Visa and Mastercard
    }

    return supportAmexCard ? tarjeta : tarjetaNoAmex; // Show either with or without Amex
  }, [features.CYBERSOURCE_AVAILABLE_CARD_BRANDS, supportAmexCard]);

  /**
   * Payment methods configuration - memoized to prevent recreation on each render
   *
   * Structure:
   * - First level keys: payment types (e.g., 'transfer', 'credit_card')
   * - Second level keys: payment providers (e.g., 'pse', 'oxxo')
   *
   * Each payment method contains:
   * - img: Logo image path
   * - label: Translated text to display
   * - isNew: Whether to show the "new" badge
   * - Additional properties like 'images' for multiple logos
   */
  const methods = useMemo(
    () => ({
      // Bank transfers (PSE, PIX, Nequi)
      transfer: {
        pse: {
          img: pse,
          label: t('button.payment_method', { context: 'transfer' }),
          isNew: false,
        },
        pix: {
          img: pixLogo,
          label: t('button.payment_method', { context: 'pix' }),
          isNew: false,
        },
        evertec: {
          img: pse,
          label: t('button.payment_method', { context: 'transfer' }),
          isNew: false,
        },
        nequi: {
          img: nequiLogo,
          label: t('button.payment_method', { context: 'nequi' }),
          isNew: true,
        },
      },
      // Digital wallets
      wallet: {
        mercadopago: {
          img: mercadopago,
          label: t('button.payment_method', { context: 'wallet' }),
          isNew: true,
        },
      },
      otp: {
        mercadopago: {
          img: yape,
          label: t('button.payment_method', { context: 'yape' }),
          isNew: true,
        },
      },
      // Physical store payments (OXXO, PayCash)
      store: {
        isStoreIcon: true,
        label: t('button.payment_method', { context: 'store' }),
        isNew: false,
        oxxo: {
          img: oxxo,
          label: t('button.payment_method', { context: 'oxxo' }),
          isNew: false,
        },
        paycash: {
          label: t('button.payment_method', { context: 'store' }),
          isNew: true,
          images: PAY_CASH_IMAGES,
          inlineImages: true,
        },
      },
      // Credit and debit cards
      credit_card: {
        img: getCardLogo(),
        label: t('button.payment_method', { context: 'credit' }),
        isNew: false,
        evertec: {
          img: supportAmexCard ? tarjeta : tarjetaNoAmex,
          label: t('button.payment_method', { context: 'credit' }),
          isNew: false,
        },
        cielo: {
          img: tarjetaPt,
          label: t('button.payment_method', { context: 'credit' }),
          isNew: false,
        },
      },
      // Third-party payment methods
      third_party: {
        coppel_pay: {
          img: coppelPay,
          label: t('button.payment_method', { context: 'coppelpay' }),
          isNew: true,
        },
      },
      // Buy now, pay later options
      bnpl: {
        kueski: {
          isNew: true,
          img: kueskipay,
          label: t('button.payment_method', { context: 'loan' }),
        },
        aplazo: {
          isNew: true,
          img: aplazo,
          label: t('button.payment_method', { context: 'biweekly' }),
        },
      },
      // The following are legacy payment types that don't follow the type/provider pattern
      // They are kept for backward compatibility
      paypal: {
        img: paypal,
        label: t('button.payment_method', { context: 'paypal' }),
        isNew: false,
      },
      apple_pay: {
        img: applePayLogo,
        label: t('button.payment_method', { context: 'apple_pay' }),
        isNew: true,
      },
      google_pay: {
        img: googlePayLogo,
        label: t('button.payment_method', { context: 'google_pay' }),
        isNew: true,
      },
      paycash: {
        label: t('button.payment_method', { context: 'store' }),
        isNew: true,
        images: PAY_CASH_IMAGES,
        inlineImages: true,
      },
      kueskipay: {
        img: kueskipay,
        label: t('button.payment_method', { context: 'loan' }),
        isNew: true,
      },
      reservamos_pay: {
        img: viajamas,
        label: t('button.payment_method', { context: 'reservamos_pay' }),
        isNew: false,
      },
      efecty: {
        img: efectyLogo,
        label: t('button.payment_method', { context: 'efecty' }),
        isNew: false,
      },
    }),
    [t, supportAmexCard, getCardLogo],
  );

  /**
   * Process and combine all payment methods from different sources
   * into a unified list for rendering.
   */
  const payments = useMemo(() => {
    // Parse legacy availablePayments format (string array)
    // Filter out any payment methods that already exist in the new format
    const parsedAvailablePayments = availablePayments
      .filter(
        (payment) =>
          !paymentMethods.some((method) => method.type === payment || method.provider === payment),
      )
      .map((payment) => ({ type: payment }));

    // Parse Yuno payment methods if available
    // Yuno has its own format that needs to be converted to our internal format
    const parsedYunoPayments =
      yuno?.availableMethods?.map((method) => {
        const category = method?.category?.toLowerCase() || '';
        // Map Yuno's payment categories to our internal payment types
        const type = CATEGORY_TYPE_MAP[category] || category;
        // Special case: show Yape logo for PagoEfectivo when feature flag is enabled
        const img = showYapeLogo && method.name === 'PagoEfectivo' ? yape : method.icon;

        return {
          label: t(
            // Special label for buy now, pay later options
            category === 'buy_now_pay_later' ? 'button.payment_bnpl' : 'button.payment_method',
            { context: 'yuno_default' },
          ),
          provider: 'yuno',
          engine: method.type.toLowerCase(),
          type,
          img,
          enabled: method.checkout.conditions.enabled,
          isNew: method.isNew || false,
        };
      }) || [];

    // Combine all payment methods from different sources
    // When PAYMENT_METHODS_AVAILABILITY_BY_COUNTRY_ENABLED is true,
    // only use new format payment methods (ignore legacy availablePayments)
    let allPayments = features.PAYMENT_METHODS_AVAILABILITY_BY_COUNTRY_ENABLED
      ? [...paymentMethods, ...parsedYunoPayments]
      : [...parsedAvailablePayments, ...paymentMethods, ...parsedYunoPayments];

    // Filter out Apple Pay if device is not compatible.
    // Google Pay is no longer pre-filtered by WebView for any engine: each SDK
    // resolves real availability at mount time. Stripe uses the ECE `ready`
    // event (availablePaymentMethods) and Adyen uses the Google Pay component's
    // `isAvailable()` (backed by the Google Pay API `isReadyToPay`); in both
    // cases an unavailable wallet (unsupported device/browser/webview, iOS
    // webviews, unverified domain, unsupported currency) self-hides in the form.
    // Delegating to the SDK avoids UA sniffing and keeps both engines symmetric.
    //
    // apple_pay still applies isApplePayAvailable() to ALL engines: it's a native
    // device capability check (ApplePaySession.canMakePayments()), not UA sniffing,
    // and Stripe's ECE `ready` event remains the definitive signal inside the form.
    allPayments = allPayments.filter((payment) => {
      if (payment.type === 'apple_pay') {
        return isApplePayAvailable();
      }
      return true;
    });

    // Change app_notify type to transfer
    allPayments = allPayments.map((payment) => {
      if (payment.type === 'app_notify') {
        return {
          ...payment,
          type: 'transfer',
        };
      }
      return payment;
    });

    // A/B test (GrowthBook `use_stripe`): the backend may expose several
    // methods of the same type with different engines (e.g. adyen + stripe for
    // credit_card or google_pay). The single flag covers the whole Stripe
    // experience: it filters every STRIPE_CAPABLE_TYPES method by engine.
    // The filter is only applied when GrowthBook resolved the flag to an
    // explicit boolean:
    // - true and Stripe available → keep only the Stripe engine for each type
    //   that has a Stripe variant.
    // - true but Stripe not available → hide Stripe, keep the tenant defaults.
    // - false → hide Stripe, keep the tenant's default engine(s).
    // Types without a Stripe variant in the list are never filtered.
    // When the flag is undefined/null (GrowthBook not ready or feature absent)
    // we don't filter: every available method is shown as-is, whether the
    // backend sends more than one method per type or only Stripe.
    const stripeAvailable =
      Boolean(stripe?.enabled) &&
      allPayments.some((p) => STRIPE_CAPABLE_TYPES.includes(p.type) && p.engine === 'stripe');

    if (typeof useStripe === 'boolean') {
      const useStripeVariant = useStripe && stripeAvailable;
      const combinedPayments = allPayments;
      allPayments = allPayments.filter((payment) => {
        if (!STRIPE_CAPABLE_TYPES.includes(payment.type)) return true;
        const typeHasStripe = combinedPayments.some(
          (p) => p.type === payment.type && p.engine === 'stripe',
        );
        if (!typeHasStripe) return true;
        const isStripe = payment.engine === 'stripe';
        return useStripeVariant ? isStripe : !isStripe;
      });
    }

    // Move default payment option to the first position if feature is enabled
    if (features.DEFAULT_PAYMENT_OPTION_FIRST) {
      allPayments = moveObjToFirst(allPayments, 'type', defaultPaymentOption);
    }

    return allPayments;
  }, [
    availablePayments,
    paymentMethods,
    yuno?.availableMethods,
    showYapeLogo,
    t,
    features.DEFAULT_PAYMENT_OPTION_FIRST,
    features.PAYMENT_METHODS_AVAILABILITY_BY_COUNTRY_ENABLED,
    defaultPaymentOption,
    useStripe,
    stripe?.enabled,
  ]);

  // Sync selectedOption with selectedPaymentMethod when needed
  useEffect(() => {
    // Only run if selectedOption exists but selectedPaymentMethod doesn't
    if (selectedOption && !selectedPaymentMethod?.provider) {
      // Try to find a payment with matching type but no engine
      let paymentToSelect = payments.find(
        (payment) => payment.type === selectedOption && !payment.engine,
      );

      // If not found, get the first payment with matching type
      if (!paymentToSelect) {
        paymentToSelect = payments.find((payment) => payment.type === selectedOption);
        // If a suitable payment was found, select it
        if (paymentToSelect) {
          const isNewStructure = Boolean(paymentToSelect.type && paymentToSelect.provider);
          onPaymentTypeChange(paymentToSelect.type, isNewStructure ? paymentToSelect : undefined);
        }
      }
    }
  }, [selectedOption, selectedPaymentMethod, payments, onPaymentTypeChange]);

  // Check if the selected payment method is still available
  useEffect(() => {
    if (features.PAYMENT_METHODS_AVAILABILITY_BY_COUNTRY_ENABLED && payments.length > 0) {
      // Wait until GrowthBook is settled so the one-shot init picks from the
      // already-filtered list (avoids selecting a non-Stripe engine before the
      // `use_stripe` value resolves).
      if (!hasInitializedRef.current && growthBookSettled) {
        hasInitializedRef.current = true;
        // First, try to find the default payment method configured in defaultPaymentOption
        const defaultConfiguredPayment = payments.find((p) => p.type === defaultPaymentOption);
        // Use the default if it exists, otherwise use the first payment in the list
        const targetPayment = defaultConfiguredPayment || payments[0];
        const isNewStructure = Boolean(targetPayment.type && targetPayment.provider);
        onPaymentTypeChange(targetPayment.type, isNewStructure ? targetPayment : undefined);
      }
    } else if (
      selectedPaymentMethod &&
      (selectedPaymentMethod.type || selectedPaymentMethod.provider)
    ) {
      const isMethodAvailable = payments.some((payment) => {
        const sameType = payment.type === selectedPaymentMethod.type;
        const sameProvider =
          payment.provider === selectedPaymentMethod.provider ||
          (!payment.provider && !selectedPaymentMethod.provider);
        return sameType && sameProvider;
      });

      // If the selected method is no longer available, look for the default or first one
      if (!isMethodAvailable && payments.length > 0) {
        // First, look for the default payment method configured in defaultPaymentOption
        const defaultConfiguredPayment = payments.find((p) => p.type === defaultPaymentOption);

        // Use the default if it exists, or the first available if not
        const defaultPayment = defaultConfiguredPayment || payments[0];

        // Select the new payment method
        const isNewStructure = Boolean(defaultPayment.type && defaultPayment.provider);
        onPaymentTypeChange(defaultPayment.type, isNewStructure ? defaultPayment : undefined);
      }
    }
  }, [
    payments,
    selectedPaymentMethod,
    defaultPaymentOption,
    onPaymentTypeChange,
    features.PAYMENT_METHODS_AVAILABILITY_BY_COUNTRY_ENABLED,
    growthBookSettled,
  ]);

  /**
   * Returns a function that handles the selection of a payment method.
   *
   * @param {Object} payment - The payment method object.
   * @param {Function} closeModal - Optional function to close the modal after selection.
   * @returns {Function} Handler function for the payment selection.
   */
  const handleSelectPayment = (payment, closeModal) => (event) => {
    // Check if the payment uses the new object structure with type and provider
    const isNewStructure = Boolean(payment.type && payment.provider);

    // Call the parent callback with the ORIGINAL payment type for payload
    // The UI mapping will be handled separately in PaymentForm
    onPaymentTypeChange(payment.type, isNewStructure ? payment : undefined);

    // If PayPal V6 is selected, trigger eligibility check only if not already loading
    if (payment.type === 'paypal' && isPayPalV6Enabled() && !isPaypalEligibilityLoading) {
      dispatch(checkPaypalEligibility());
    }
    // Close the modal if provided (used when rendered inside a modal)
    if (closeModal) closeModal();

    // Scroll the selected payment card into view, centered in the container
    const cardElement = event?.currentTarget?.closest?.('.payment-selector-badge');
    if (cardElement) {
      cardElement.scrollIntoView({
        inline: 'center',
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  };

  /**
   * Helper function to render the payment method card UI
   */
  const renderMethodCard = ({ method, payment, closeModal }) => {
    const { type, provider } = payment;
    const isNew = payment.isNew || method.isNew || false;
    const isActive =
      (selectedPaymentMethod?.provider === provider && selectedPaymentMethod?.type === type) ||
      (!provider && type === selectedOption);

    return (
      <div key={`${type}-${provider || ''}`} className="payment-selector-badge">
        {isNew && (
          <div className="payment-selector-badge-wrapper">
            <BadgeNew />
          </div>
        )}
        <ButtonCard
          imgSrc={method.img}
          images={method.images}
          isStoreIcon={method.isStoreIcon}
          inlineImages={method.inlineImages}
          onClick={handleSelectPayment(payment, closeModal)}
          isActive={isActive}
        >
          <Text color="grayMedium" size="XS" whiteSpace="nowrap">
            {method.label}
          </Text>
        </ButtonCard>
      </div>
    );
  };

  /**
   * Renders a single payment method card based on the payment configuration.
   * This function handles different payment method types and their specific requirements:
   * - Yuno payment methods (special handling for gateway integration)
   * - PIX payments (direct bank transfers)
   * - BNPL methods (requiring specific provider configuration)
   * - Legacy payment methods (backward compatibility)
   * - Standard payment methods with optional provider configurations
   *
   * @param {Object} payment - The payment method object containing type, provider, and other configuration
   * @param {Function} closeModal - Optional callback to close the modal after selection
   * @returns {React.Element|null} The rendered payment card or null if the method shouldn't be displayed
   */
  const renderPaymentCard = (payment, closeModal) => {
    const { type, provider } = payment;

    // Check if this is a Yuno payment method and get its configuration
    const isYuno = Boolean(payment.provider === 'yuno');
    if (isYuno) {
      // Skip if it's a Yuno engine method or if the method is not enabled
      if (payment.engine === 'yuno' || !payment.enabled) return null;
      return renderMethodCard({
        method: { img: payment.img, label: payment.label },
        payment,
        closeModal,
      });
    }

    // Get the base configuration for this payment type from our methods mapping
    const methodType = methods[type];

    // Handle PIX special case
    if (type === 'pix') {
      return renderMethodCard({
        method: methods.transfer.pix,
        payment,
        closeModal,
      });
    }

    // Skip rendering if the payment type doesn't exist in our configuration
    // This prevents displaying methods that aren't properly configured
    if (!methodType) return null;

    // List of payment types that require specific provider configuration
    // These methods must have a valid provider to be displayed
    const requiresProviderConfig = ['bnpl'];

    // Get the appropriate method configuration
    let method;

    if (requiresProviderConfig.includes(type)) {
      // For payment types that require specific provider configuration (e.g., BNPL)
      // Skip if no provider is specified or if the provider doesn't exist in the configuration
      if (!provider || !methodType[provider]) return null;
      method = methodType[provider];
    } else if (provider && methodType[provider]) {
      // If provider exists and has specific config, use it
      method = methodType[provider];
    } else if (methods[type]?.img || methods[type]?.label || methods[type]?.images) {
      // If type exists as a direct configuration (like paypal)
      method = methods[type];
    } else {
      // Use general type config
      method = methodType;
    }

    // Special case for old-structure transfer payments - use PSE config
    if (!provider && type === 'transfer') {
      method = methodType.pse;
    }

    return renderMethodCard({ method, payment, closeModal });
  };

  /**
   * Renders all payment method cards in a grid.
   *
   * @param {Function} closeModal - Optional function to close the modal after selection.
   * @returns {React.Element} Container with all payment method cards.
   */
  const renderPaymentCards = (closeModal) => (
    <div className="payment-selector">
      {payments.map((payment) => renderPaymentCard(payment, closeModal))}
    </div>
  );

  // Don't render the component if there are no payment methods available
  // No selection is needed in that case
  if (payments.length === 0) return null;
  return (
    <Spacing vertical size="S" responsiveSize="XS">
      {/* Component title */}
      <Text weight="bold" size="XL">
        {t('select_payment_method')}
      </Text>

      {/* Optional payment disclaimer based on feature flag */}
      {features.SHOW_PAYMENT_DISCLAIMER && (
        <MessageBox borderColor="primary">
          <Text>
            <Trans t={t} i18nKey="text.payment_disclaimer" tOptions={{ context: brand }}>
              text<b>text</b>text
            </Trans>
          </Text>
        </MessageBox>
      )}
      {/* Render the payment method selection grid */}
      {renderPaymentCards()}
    </Spacing>
  );
};

PaymentSelector.propTypes = {
  selectedOption: PropTypes.string,
  availablePayments: PropTypes.array,
  onPaymentTypeChange: PropTypes.func,
  paymentMethods: PropTypes.array,
};

PaymentSelector.defaultProps = {
  selectedOption: '',
  availablePayments: [],
  onPaymentTypeChange: () => {},
};

export default PaymentSelector;
